import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Download, FileText, Loader2, Search, Upload } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/AppShell";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { api, apiError, formatDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PARTNER_NAV } from "./nav";

export default function PartnerDashboard() {
  const { user } = useAuth();
  const fileRef = useRef(null);
  const [students, setStudents] = useState(null);
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [plan, setPlan] = useState(null);
  const supportRef = useRef(null);
  const [supportKind, setSupportKind] = useState("Gradebook report");
  const [supporting, setSupporting] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/partner/students", { params: { search } });
    setStudents(data);
  }, [search]);

  useEffect(() => {
    const t = setTimeout(() => load().catch((e) => toast.error(apiError(e))), 250);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    api
      .get("/partner/subscription")
      .then(({ data }) => setPlan(data))
      .catch(() => {});
  }, []);

  const template = async (fmt) => {
    try {
      const res = await api.get(`/partner/records-template/${fmt}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ACS-Partner-Annual-Academic-Records-Submission-Package.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const uploadSupporting = async (file) => {
    if (!file) return;
    setSupporting(true);
    try {
      const body = new FormData();
      body.append("file", file);
      body.append("kind", supportKind);
      await api.post(`/partner/students/${detail.student.id}/supporting-files`, body);
      toast.success("Supporting file added");
      openDetail(detail.student.id);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSupporting(false);
      if (supportRef.current) supportRef.current.value = "";
    }
  };

  const openDetail = async (studentId) => {
    try {
      const { data } = await api.get(`/partner/students/${studentId}`);
      setDetail(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const upload = async (file) => {
    if (!file || !detail) return;
    setUploading(true);
    try {
      const body = new FormData();
      body.append("file", file);
      const { data } = await api.post(
        `/partner/students/${detail.student.id}/upload`,
        body,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setDetail(data);
      toast.success("Records package submitted to Alabama Church School");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const downloadDoc = async (recordId, index, filename) => {
    const { data } = await api.get(`/partner/records/${recordId}/documents/${index}/download`, {
      responseType: "blob",
    });
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const needsCorrection = (students || []).filter((s) => s.record_status === "Needs Correction");

  return (
    <AppShell
      nav={PARTNER_NAV}
      title={user?.partner?.name ? `${user.partner.name} Portal` : "Partner Portal"}
      subtitle="Upload annual academic records packages for students enrolled with Alabama Church School"
    >
      {plan && !plan.can_work && (
        <div
          className="mb-5 flex items-start gap-3 rounded-md border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-800"
          data-testid="dashboard-lapsed-banner"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            Your annual ACS partner registration is not current, so uploading records and adding
            students is paused. Your existing records are safe.{" "}
            <Link to="/partner/plan" className="font-semibold underline">
              Review your plan
            </Link>
            .
          </div>
        </div>
      )}
      {plan && plan.can_work && plan.at_limit && (
        <div
          className="mb-5 flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-900"
          data-testid="dashboard-limit-banner"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            You have used all {plan.student_limit} active student places on {plan.plan_name}.{" "}
            <Link to="/partner/plan" className="font-semibold underline">
              Upgrade your plan
            </Link>{" "}
            to add more. Nothing is deleted.
          </div>
        </div>
      )}
      {needsCorrection.length > 0 && (
        <div className="mb-5 flex items-start gap-3 rounded-md border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700" data-testid="correction-banner">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            {needsCorrection.length} student record(s) need a correction from ACS. Open the student to
            read the note and upload a corrected package.
          </div>
        </div>
      )}

      <div className="mb-5 flex flex-wrap items-center gap-3">
        <div className="relative w-full max-w-sm">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
          <Input
            className="pl-9"
            placeholder="Search my students…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="partner-search-input"
          />
        </div>
        <Link
          to="/partner/how-it-works"
          className="text-xs font-semibold text-blue-700 hover:underline"
          data-testid="how-it-works-link"
        >
          New here? Read how to upload records →
        </Link>
      </div>

      <Card className="border-slate-200">
        <CardContent className="overflow-x-auto p-0">
          {!students ? (
            <div className="p-6">
              <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
            </div>
          ) : (
            <Table data-testid="partner-students-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Student</TableHead>
                  <TableHead>DOB</TableHead>
                  <TableHead>Grade</TableHead>
                  <TableHead>Academic year</TableHead>
                  <TableHead>Record status</TableHead>
                  <TableHead>Uploads</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {students.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-sm text-slate-500">
                      No students assigned yet. Students appear here automatically once a family
                      enrolls with Alabama Church School through your enrollment page.
                    </TableCell>
                  </TableRow>
                )}
                {students.map((s) => (
                  <TableRow key={s.id} data-testid={`partner-student-row-${s.id}`}>
                    <TableCell className="font-medium text-slate-900">{s.legal_name}</TableCell>
                    <TableCell className="text-sm text-slate-600">{s.date_of_birth || "—"}</TableCell>
                    <TableCell className="text-sm">{s.grade_level || "—"}</TableCell>
                    <TableCell className="text-sm">{s.academic_year || "—"}</TableCell>
                    <TableCell>
                      <StatusBadge status={s.record_status} testId={`partner-status-${s.id}`} />
                    </TableCell>
                    <TableCell>{s.versions}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-2"
                        onClick={() => openDetail(s.id)}
                        data-testid={`open-student-${s.id}`}
                      >
                        <FileText className="h-4 w-4" /> Open
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={Boolean(detail)} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>{detail?.student?.legal_name}</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-6">
              <div className="flex flex-wrap items-center gap-3 text-sm text-slate-600">
                <StatusBadge status={detail.record.status} testId="detail-status" />
                <span>
                  {detail.student.grade_level} · {detail.student.academic_year}
                </span>
              </div>

              {detail.record.correction_note && (
                <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="detail-correction-note">
                  <strong>ACS correction note:</strong> {detail.record.correction_note}
                </div>
              )}

              {detail.record.submission_gaps?.length > 0 && (
                <div
                  className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
                  data-testid="detail-submission-gaps"
                >
                  <strong>Incomplete — partner action needed.</strong> The ACS package is missing:
                  <ul className="ml-4 mt-1 list-disc">
                    {detail.record.submission_gaps.map((g) => (
                      <li key={g}>{g}</li>
                    ))}
                  </ul>
                  Complete these fields and upload the package again.
                </div>
              )}

              <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-4">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-blue-700">
                  ACS submission package template
                </h3>
                <p className="mt-1 text-xs text-slate-600">
                  Complete one ACS package per student, then upload it below. Download it as a
                  fillable PDF or an Excel workbook — whichever you prefer.
                </p>
                <p className="mt-1.5 text-xs font-medium text-amber-700">
                  Please complete it on screen and save — a printed, hand-written and scanned copy
                  cannot be read reliably.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-2"
                    onClick={() => template("pdf")}
                    data-testid="download-template-pdf"
                  >
                    <Download className="h-3.5 w-3.5" /> Fillable PDF
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-2"
                    onClick={() => template("xlsx")}
                    data-testid="download-template-xlsx"
                  >
                    <Download className="h-3.5 w-3.5" /> Excel workbook
                  </Button>
                </div>
              </div>

              <div>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".pdf,.xlsx,.xlsm,application/pdf"
                  className="hidden"
                  onChange={(e) => upload(e.target.files?.[0])}
                  data-testid="partner-upload-input"
                />
                <Button
                  className="gap-2"
                  disabled={uploading}
                  onClick={() => fileRef.current?.click()}
                  data-testid="partner-upload-button"
                >
                  {uploading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4" />
                  )}
                  {detail.record.versions.length
                    ? "Upload Revised Annual Records"
                    : "Upload Annual Records"}
                </Button>
                <p className="mt-2 text-xs text-slate-500">
                  Accepted: the completed ACS package (PDF or Excel), or your own equivalent
                  records package. Previous uploads are retained in the record history.
                </p>
                {detail.record.extraction_status === "processing" && (
                  <p className="mt-1 text-xs text-blue-700" data-testid="partner-extraction-processing">
                    Alabama Church School is reading the uploaded package…
                  </p>
                )}
              </div>

              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-blue-700">
                  Upload history
                </h3>
                <ul className="mt-2 divide-y divide-slate-100" data-testid="detail-versions">
                  {detail.record.versions.length === 0 && (
                    <li className="py-2 text-sm text-slate-500">No uploads yet.</li>
                  )}
                  {detail.record.versions.map((v) => (
                    <li key={v.version} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
                      <span>
                        v{v.version} · {v.filename}
                      </span>
                      <span className="text-xs text-slate-500">{formatDate(v.uploaded_at)}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-blue-700">
                  Supporting records (optional)
                </h3>
                <p className="mt-1 text-xs text-slate-500">
                  Gradebook, assessment, attendance or curriculum reports. These are stored with
                  the record and never change the information read from the ACS package.
                </p>
                <ul className="mt-2 divide-y divide-slate-100" data-testid="detail-supporting-files">
                  {(detail.record.supporting_files || []).length === 0 && (
                    <li className="py-2 text-sm text-slate-500">None uploaded.</li>
                  )}
                  {(detail.record.supporting_files || []).map((f) => (
                    <li
                      key={f.index}
                      className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm"
                    >
                      <span>
                        {f.filename} <span className="text-xs text-slate-500">· {f.kind}</span>
                      </span>
                      <span className="text-xs text-slate-500">{formatDate(f.uploaded_at)}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <select
                    className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm"
                    value={supportKind}
                    onChange={(e) => setSupportKind(e.target.value)}
                    data-testid="supporting-kind-select"
                  >
                    {[
                      "Gradebook report",
                      "Assessment report",
                      "Attendance report",
                      "Curriculum completion record",
                      "Other academic documentation",
                    ].map((k) => (
                      <option key={k} value={k}>
                        {k}
                      </option>
                    ))}
                  </select>
                  <input
                    ref={supportRef}
                    type="file"
                    className="hidden"
                    onChange={(e) => uploadSupporting(e.target.files?.[0])}
                    data-testid="supporting-file-input"
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-2"
                    disabled={supporting}
                    onClick={() => supportRef.current?.click()}
                    data-testid="supporting-upload-button"
                  >
                    {supporting ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Upload className="h-3.5 w-3.5" />
                    )}
                    Add supporting file
                  </Button>
                </div>
              </div>

              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-blue-700">
                  Final ACS documents
                </h3>
                <ul className="mt-2 divide-y divide-slate-100" data-testid="detail-documents">
                  {detail.record.documents.length === 0 && (
                    <li className="py-2 text-sm text-slate-500">
                      No ACS document has been issued yet.
                    </li>
                  )}
                  {detail.record.documents.map((d, i) => (
                    <li key={d.doc_number} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
                      <span className="font-medium text-slate-900">{d.doc_number}</span>
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-2"
                        onClick={() => downloadDoc(detail.record.id, i, d.filename)}
                        data-testid={`partner-download-document-${i}`}
                      >
                        <Download className="h-3.5 w-3.5" /> Download
                      </Button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetail(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
